// 📊 Advanced Header Component with Navigation & User Features
// React Patterns: React.memo + Custom Hooks + Search + Notifications + Theme Toggle

import React, { memo, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useUI } from '../store/AppContext';
import { useAdminNavigate } from '../navigation/useAdminNavigate';
import { menuIdToPath } from '../navigation/adminRoutes';
import {
  prepareSearchNavigation,
  searchAdminIndex,
} from '../navigation/adminSearchIndex';
import { canAccessMenu } from '../security/acl';
import { log } from '../utils/log';
import { clearAdminStaffTokenCookie } from '../utils/adminStaffTokenCookie';
import {
  formatNotificationRelativeTime,
  getNotificationTargetMenuId,
  getNotificationTypeMeta,
} from '../features/notifications/notificationUtils';

function resultTypeMeta(type) {
  if (type === 'setting') {
    return { badgeClass: 'bg-amber-100 text-amber-700', icon: 'fa-cog', label: 'Setting' };
  }
  return { badgeClass: 'bg-indigo-100 text-indigo-700', icon: 'fa-compass', label: 'Page' };
}

// Magento-style global admin search (menus + settings tabs + keywords)
const SearchBar = memo(() => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const { goToMenu, navigate } = useAdminNavigate();
  const { setActiveMenu, currentUser } = useUI();

  const runSearch = useCallback(
    (searchQuery) => {
      const q = String(searchQuery || '').trim();
      if (q.length < 2) {
        setResults([]);
        setIsOpen(false);
        setHighlightIndex(-1);
        return;
      }
      const next = searchAdminIndex(q, { limit: 24 }).filter((item) => {
        if (!item.menuId) return true;
        return canAccessMenu(currentUser, item.menuId);
      }).slice(0, 12);
      setResults(next);
      setIsOpen(true);
      setHighlightIndex(next.length > 0 ? 0 : -1);
    },
    [currentUser]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      runSearch(query);
    }, 150);
    return () => clearTimeout(timer);
  }, [query, runSearch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigateToResult = useCallback(
    (result) => {
      if (!result) return;
      const path = prepareSearchNavigation(result);
      setQuery('');
      setIsOpen(false);
      setResults([]);
      setHighlightIndex(-1);

      if (result.menuId) {
        setActiveMenu(result.menuId);
      }

      if (path) {
        navigate(path);
      } else if (result.menuId) {
        goToMenu(result.menuId);
      } else {
        log.warn('Search result missing path/menuId', result);
      }

      if (result.tab && result.menuId === 'email-settings') {
        window.dispatchEvent(
          new CustomEvent('admin:open-email-settings-tab', { detail: { tab: result.tab } })
        );
      } else if (result.tab && result.menuId === 'settings') {
        window.dispatchEvent(
          new CustomEvent('admin:open-settings-tab', { detail: { tab: result.tab } })
        );
      }
    },
    [goToMenu, navigate, setActiveMenu]
  );

  const handleInputChange = (e) => {
    setQuery(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (!isOpen && results.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = results[highlightIndex] || results[0];
      if (pick) navigateToResult(pick);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const showEmpty = isOpen && query.trim().length >= 2 && results.length === 0;
  const showDropdown = isOpen && (results.length > 0 || showEmpty);

  return (
    <div ref={searchRef} className="relative flex-1 max-w-lg">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => {
            if (query.trim().length >= 2) setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Search pages & settings…"
          aria-label="Search admin pages and settings"
          aria-autocomplete="list"
          aria-expanded={showDropdown}
          className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-200 bg-white text-slate-800 placeholder:text-slate-400 shadow-sm"
        />
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <i className="fas fa-search text-gray-400"></i>
        </div>
      </div>

      {showDropdown && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl max-h-96 overflow-y-auto">
          {showEmpty ? (
            <div className="px-4 py-6 text-center text-sm text-slate-500">
              No pages or settings match “{query.trim()}”
            </div>
          ) : (
            <div className="py-2">
              <div className="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Pages & settings
              </div>
              {results.map((result, index) => {
                const meta = resultTypeMeta(result.type);
                const active = index === highlightIndex;
                return (
                  <button
                    key={result.id}
                    type="button"
                    onMouseEnter={() => setHighlightIndex(index)}
                    onClick={() => navigateToResult(result)}
                    className={`w-full px-4 py-2.5 text-left flex items-center space-x-3 transition-colors duration-150 ${
                      active ? 'bg-indigo-50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${meta.badgeClass}`}
                    >
                      <i className={`fas ${result.icon || meta.icon} text-sm`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">{result.title}</div>
                      <div className="text-sm text-gray-500 truncate">{result.subtitle}</div>
                    </div>
                    <div className="text-xs text-gray-400 uppercase shrink-0">{meta.label}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

SearchBar.displayName = 'SearchBar';

// 🔔 Notifications Dropdown Component
const NotificationsDropdown = memo(() => {
  const [isOpen, setIsOpen] = useState(false);
  const [realtimeStatus, setRealtimeStatus] = useState('connecting');
  const [lastEvent, setLastEvent] = useState('—');
  const { notifications, markNotificationRead } = useUI();
  const { goToMenu } = useAdminNavigate();
  const dropdownRef = useRef(null);
  const openRelevantPage = (notification) => {
    if (notification?.id != null) markNotificationRead(notification.id);
    goToMenu(getNotificationTargetMenuId(notification));
    setIsOpen(false);
  };


  // 🎯 Handle outside clicks
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const onStatus = (e) => setRealtimeStatus(e?.detail?.status || 'unknown');
    const onEvent = (e) => setLastEvent(e?.detail?.event || 'EVENT');
    window.addEventListener('admin:realtime-status', onStatus);
    window.addEventListener('admin:broadcast-event', onEvent);
    return () => {
      window.removeEventListener('admin:realtime-status', onStatus);
      window.removeEventListener('admin:broadcast-event', onEvent);
    };
  }, []);

  // 📊 Memoized unread count
  const unreadCount = useMemo(() => {
    if (!Array.isArray(notifications)) return 0;
    return notifications.filter(n => !n.read).length;
  }, [notifications]);

  const toggleDropdown = () => setIsOpen(!isOpen);

  const latestNotifications = Array.isArray(notifications)
    ? [...notifications].slice(-20).reverse()
    : [];

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={toggleDropdown}
        className="relative p-2.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-colors duration-200"
      >
        <i className="fas fa-bell text-lg"></i>
        <span
          className={`absolute -bottom-1 -left-1 h-2.5 w-2.5 rounded-full ${
            realtimeStatus === 'connected'
              ? 'bg-green-500'
              : realtimeStatus === 'connecting'
                ? 'bg-yellow-500'
                : 'bg-red-500'
          }`}
        />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-xl z-50">
          <div className="p-4 border-b border-slate-200">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            <div className="text-xs text-gray-500 mt-1">
              Live: {realtimeStatus} · Last event: {lastEvent}
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {latestNotifications.length > 0 ? (
              <div className="py-2">
                {latestNotifications.map((notification) => {
                  const meta = getNotificationTypeMeta(notification.type);
                  return (
                  <div
                    key={notification.id}
                    role="button"
                    tabIndex={0}
                    className={`p-4 hover:bg-slate-50 transition-colors duration-150 border-l-4 cursor-pointer ${!notification.read ? `${meta.borderClass} bg-indigo-50/50` : 'border-transparent'
                      }`}
                    onClick={() => openRelevantPage(notification)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openRelevantPage(notification);
                      }
                    }}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${meta.badgeClass}`}>
                        <i className={`fas ${meta.icon} text-sm`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`font-medium ${!notification.read ? 'text-gray-900' : 'text-gray-700'}`}>
                          {notification.title}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {notification.message}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {formatNotificationRelativeTime(notification.timestamp)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
                })}
              </div>
            ) : (
              <div className="p-4 text-center text-sm text-gray-500">No new notifications</div>
            )}
          </div>

          <div className="p-4 border-t border-slate-200">
            <button
              className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
              onClick={() => {
                goToMenu('notification-center');
                setIsOpen(false);
              }}
            >
              View all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

NotificationsDropdown.displayName = 'NotificationsDropdown';

// 👤 User Profile Dropdown Component
const UserProfileDropdown = memo(() => {
  const [isOpen, setIsOpen] = useState(false);
  const { theme, setTheme, currentUser } = useUI();
  const { goToMenu } = useAdminNavigate();
  const dropdownRef = useRef(null);

  // 🎯 Handle outside clicks
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => setIsOpen(!isOpen);
  const toggleTheme = () => setTheme(theme === 'light' ? 'dark' : 'light');
  const openMenu = useCallback(
    (menuId) => {
      setIsOpen(false);
      goToMenu(menuId);
    },
    [goToMenu]
  );

  const handleSignOut = useCallback(() => {
    setIsOpen(false);
    ['admin-acl-user', 'admin-acl-users', 'admin-acl-roles'].forEach((k) => localStorage.removeItem(k));
    try {
      sessionStorage.removeItem('admin-staff-jwt');
      sessionStorage.removeItem('admin-staff-user-id');
      clearAdminStaffTokenCookie();
    } catch {
      /* ignore */
    }
    localStorage.setItem('admin-signed-out', '1');
    window.dispatchEvent(new CustomEvent('admin:session-signed-out'));
  }, []);

  const userName = currentUser?.name || 'Admin User';
  const userEmail = currentUser?.email || 'admin@tejasfleets.com';
  const userRole = String(currentUser?.role || 'super_admin')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={toggleDropdown}
        className="flex items-center space-x-3 p-2.5 text-slate-700 hover:bg-slate-100 rounded-xl transition-colors duration-200"
      >
        <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
          <i className="fas fa-user text-white text-sm"></i>
        </div>
        <div className="hidden md:block text-left">
          <div className="font-medium text-sm">{userName}</div>
          <div className="text-xs text-gray-500">{userEmail}</div>
        </div>
        <i className="fas fa-chevron-down text-xs text-gray-400"></i>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-xl shadow-xl z-50">
          <div className="p-4 border-b border-slate-200">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center">
                <i className="fas fa-user text-white"></i>
              </div>
              <div>
                <div className="font-medium text-gray-900">{userName}</div>
                <div className="text-sm text-gray-500">{userEmail}</div>
                <div className="text-xs text-indigo-600 mt-0.5">Role: {userRole}</div>
              </div>
            </div>
          </div>

          <div className="py-2">
            <button
              type="button"
              onClick={() => openMenu('profile-settings')}
              className="w-full px-4 py-2.5 text-left hover:bg-slate-50 flex items-center space-x-3 transition-colors duration-150"
            >
              <i className="fas fa-user-circle text-gray-400 w-5"></i>
              <span className="text-gray-700">Profile Settings</span>
            </button>

            <button
              type="button"
              onClick={() => openMenu('change-password')}
              className="w-full px-4 py-2.5 text-left hover:bg-slate-50 flex items-center space-x-3 transition-colors duration-150"
            >
              <i className="fas fa-key text-gray-400 w-5"></i>
              <span className="text-gray-700">Change Password</span>
            </button>

            <button
              type="button"
              onClick={toggleTheme}
              className="w-full px-4 py-2.5 text-left hover:bg-slate-50 flex items-center justify-between transition-colors duration-150"
              aria-pressed={theme === 'dark'}
            >
              <div className="flex items-center space-x-3">
                <i className={`fas ${theme === 'light' ? 'fa-moon' : 'fa-sun'} text-gray-400 w-5`}></i>
                <span className="text-gray-700">
                  {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
                </span>
              </div>
              <div className={`w-10 h-5 rounded-full ${theme === 'dark' ? 'bg-indigo-600' : 'bg-gray-300'} relative transition-colors duration-200`}>
                <div className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-transform duration-200 ${theme === 'dark' ? 'translate-x-5' : 'translate-x-0.5'
                  }`}></div>
              </div>
            </button>

            <div className="border-t border-slate-200 my-2"></div>

            <button
              type="button"
              onClick={handleSignOut}
              className="w-full px-4 py-2.5 text-left hover:bg-red-50 flex items-center space-x-3 transition-colors duration-150 text-red-600"
            >
              <i className="fas fa-sign-out-alt w-5"></i>
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

UserProfileDropdown.displayName = 'UserProfileDropdown';

// 🍞 Breadcrumb Component
const Breadcrumb = memo(() => {
  const { sidebar } = useUI();

  // 📊 Memoized breadcrumb data
  const breadcrumbData = useMemo(() => {
    const menuLabels = {
      dashboard: 'Dashboard',
      watchtower: 'Watch Tower',
      bookings: 'Bookings',
      'booking-activity': 'Booking activity',
      'booking-invoices': 'Invoices',
      'booking-transactions': 'Transactions',
      'booking-manual-broadcast': 'Manual Broadcast',
      'guest-vendor-interests': 'Guest Vendor Interests',
      'vendor-cab-availability': 'Vendor Cab Availability',
      'booking-leads': 'User Journey Funnel',
      'booking-quotes': 'Quote',
      'booking-view': 'View Booking',
      'booking-edit': 'Edit Booking',
      vendors: 'Vendors',
      'vendor-join-us-enquiries': 'Join Us Enquiry',
      'vendor-penalties': 'Vendor penalties',
      'finance-dashboard': 'Finance dashboard',
      'finance-vehicle-ledger': 'Vehicle ledger',
      'finance-expenses': 'Expense tracking',
      'finance-fastag-import': 'FASTag import',
      'finance-zoho-export': 'Zoho Books export',
      'finance-driver-ledger': 'Driver settlements',
      'finance-vendor-finance': 'Vendor finance',
      'finance-vendor-settlement': 'Vendor settlement',
      'finance-fleet-utilization': 'Fleet utilization',
      'finance-driver-performance': 'Driver performance',
      'finance-gst-summary': 'GST summary',
      'finance-vehicle-pl': 'Vehicle income & expenses',
      'finance-vendor-payouts': 'Vendor payouts',
      'finance-driver-settlements': 'Driver settlements',
      'finance-toll-fuel': 'Toll, fuel & maintenance',
      'finance-gst-invoices': 'GST invoices',
      'finance-customer-payments': 'Customer payments',
      'finance-fleet-profitability': 'Fleet profitability',
      'finance-emi-loans': 'EMI/loan tracking',
      'finance-route-pl': 'Route profit/loss',
      'finance-checkout-addons-report': 'Checkout Add-ons Report',
      'finance-snackbox-orders-report': 'Snackbox Ordered Report',
      'finance-multi-vendor': 'Multi-vendor accounting',
      'finance-reconciliation': 'Cash & online reconciliation',
      'cabs-fleet': 'Fleet',
      'cabs-approval': 'Cabs Approval',
      'cab-expenses': 'Cab Expenses',
      'cab-revenue': 'Cab wise revenue',
      'cab-sold-out': 'Sold Out',
      'drivers-roster': 'Drivers',
      'driver-approval': 'Driver Approval',
      'driver-salary-slip': 'Salary Slip',
      'driver-expenses': 'Driver Expenses',
      pricing: 'Pricing',
      cities: 'Cities',
      'location-missing-searches': 'Missing location searches',
      'location-search-index': 'Location search index',
      'location-master-ops': 'Location master ops',
      routes: 'Routes',
      'route-pages': 'Route Pages',
      'seo-control-center': 'SEO Control Center',
      'seo-page-urls': 'Page URLs',
      sitemaps: 'Sitemap Generator',
      'robots-txt': 'Robots.txt Generator',
      'seo-settings': 'SEO Settings',
      'seo-group': 'SEO',
      'homepage-popular-routes': 'Popular Routes',
      'homepage-popular-route-categories': 'Route Categories',
      'faq-categories': 'FAQ Categories',
      'faqs': 'FAQ',
      'faq-group': 'FAQ',
      'reviews': 'Reviews',
      'segment-configuration': 'Cab Segment',
      users: 'Customers',
      'customer-activities-log': 'Activities Log',
      'customer-view': 'View Customer',
      'customer-edit': 'Edit Customer',
      'corporate-clients': 'Corporate clients',
      'corporate-pending': 'Pending approvals',
      'corporate-bookings': 'Corporate bookings',
      'corporate-pricing': 'Corporate pricing',
      'corporate-contracts': 'Contracts',
      'corporate-invoices': 'Invoices',
      'corporate-reports': 'Reports',
      'corporate-erp': 'ERP integrations',
      'corporate-client-detail': 'Client detail',
      analytics: 'Analytics Reports',
      'analytics-advanced': 'Advanced Analytics',
      'analytics-booking-funnel': 'Booking funnel',
      'analytics-cancellations': 'Cancellation reports',
      'analytics-coupon-usage': 'Coupon usage report',
      'analytics-cookie-consent': 'Cookie consent report',
      'newsletter-subscribers': 'Newsletter subscribers',
      'newsletter-campaigns': 'Newsletter campaigns',
      'newsletter-settings': 'Newsletter settings',
      logs: 'Logs',
      'qa-test-results': 'QA test results',
      'code-flow': 'Code Flow',
      'dev-doc': 'Dev Doc',
      'db-backup': 'DB Backup',
      'pricing-test': 'Booking test cases',
      'pricing-price-analysis': 'Price Analysis',
      settings: 'Settings',
      'loyalty-settings': 'Loyalty points',
      'chatbot-settings': 'Chatbot',
      'cancellation-settings': 'Cancellation configuration',
      'maintenance-settings': 'Maintenance mode',
      'database-settings': 'Database',
      'ai-seo-overview': 'AI SEO',
      'ai-seo-how-to': 'How to use AI SEO',
      'ai-seo-providers': 'AI providers',
      'ai-seo-models': 'AI models',
      'ai-seo-routing': 'AI routing',
      'ai-seo-settings': 'AI settings',
      'ai-seo-prompts': 'AI prompts',
      'ai-seo-keywords': 'AI keywords',
      'ai-seo-content': 'AI content',
      'ai-seo-calendar': 'AI calendar',
      'ai-seo-social': 'AI social',
      'ai-seo-knowledge': 'AI knowledge base',
      'ai-seo-usage': 'AI usage',
      'status-management': 'Status Management',
      'notification-center': 'Notification center',
      'settings-group': 'Settings',
      'profile-settings': 'Profile Settings',
      'change-password': 'Change Password',
      'acl-all-users': 'All admin users',
      'acl-locked-users': 'Locked admin users',
      'acl-user-roles': 'Admin roles',
      'acl-admin-access-log': 'Admin Access Log',
    };

    const homePath = menuIdToPath('dashboard');
    const currentPath = menuIdToPath(sidebar.activeMenu);
    const corporateMenus = new Set([
      'corporate-clients',
      'corporate-pending',
      'corporate-bookings',
      'corporate-pricing',
      'corporate-contracts',
      'corporate-invoices',
      'corporate-reports',
      'corporate-erp',
      'corporate-client-detail',
    ]);
    const toolsMenus = new Set([
      'pricing-test',
      'qa-test-results',
      'code-flow',
      'dev-doc',
      'db-backup',
    ]);
    const crumbs = [{ label: 'Home', to: homePath, icon: 'fa-home' }];
    if (corporateMenus.has(sidebar.activeMenu)) {
      crumbs.push({
        label: 'Corporate',
        to: menuIdToPath('corporate-clients'),
      });
    }
    if (toolsMenus.has(sidebar.activeMenu)) {
      crumbs.push({
        label: 'Tools',
        to: menuIdToPath('code-flow'),
      });
    }
    crumbs.push({
      label: menuLabels[sidebar.activeMenu] || 'Dashboard',
      to: currentPath,
      active: true,
    });
    return crumbs;
  }, [sidebar.activeMenu]);

  return (
    <nav className="flex items-center space-x-2 text-sm">
      {breadcrumbData.map((item, index) => (
        <div key={index} className="flex items-center space-x-2">
          {index > 0 && (
            <i className="fas fa-chevron-right text-gray-400 text-xs"></i>
          )}
          {item.active ? (
            <span className="flex items-center space-x-1 text-indigo-600 font-medium">
              {item.icon && <i className={`fas ${item.icon}`}></i>}
              <span>{item.label}</span>
            </span>
          ) : (
            <Link
              to={item.to}
              className="flex items-center space-x-1 text-slate-600 hover:text-slate-900 no-underline"
            >
              {item.icon && <i className={`fas ${item.icon}`}></i>}
              <span>{item.label}</span>
            </Link>
          )}
        </div>
      ))}
    </nav>
  );
});

Breadcrumb.displayName = 'Breadcrumb';

// 📊 Main Header Component
const Header = memo(() => {
  const { sidebar, toggleSidebar } = useUI();

  return (
    <header className="fixed top-0 right-0 left-0 bg-white/95 backdrop-blur border-b border-slate-200 z-30 transition-all duration-300">
      <div className={`transition-all duration-300 ${sidebar.isOpen ? 'lg:ml-64' : 'lg:ml-16'
        }`}>
        <div className="flex items-center justify-between px-6 py-4">
          {/* 🍔 Left Section - Mobile Menu + Breadcrumb */}
          <div className="flex items-center space-x-4">
            {/* Mobile Menu Button */}
            <button
              onClick={toggleSidebar}
              className="lg:hidden p-2.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-colors duration-200"
            >
              <i className="fas fa-bars text-lg"></i>
            </button>

            {/* Breadcrumb */}
            <div className="hidden sm:block">
              <Breadcrumb />
            </div>
          </div>

          {/* 🔍 Center Section - Search (Desktop) */}
          <div className="hidden md:block flex-1 max-w-2xl mx-8">
            <SearchBar />
          </div>

          {/* 🔔 Right Section - Actions */}
          <div className="flex items-center space-x-2">
            {/* Mobile Search Toggle */}
            <button className="md:hidden p-2.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-colors duration-200">
              <i className="fas fa-search text-lg"></i>
            </button>

            {/* Quick Actions */}
            <button
              className="p-2.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-colors duration-200"
              title="New Booking"
            >
              <i className="fas fa-plus text-lg"></i>
            </button>

            {/* Notifications */}
            <NotificationsDropdown />

            {/* User Profile */}
            <UserProfileDropdown />
          </div>
        </div>

        {/* 🔍 Mobile Search Bar */}
        <div className="md:hidden px-6 pb-4">
          <SearchBar />
        </div>
      </div>
    </header>
  );
});

Header.displayName = 'Header';

export default Header;